import type {
  WebsiteGraphAsset,
  WebsiteGraphLink,
  WebsiteGraphPage,
  WebsiteRelationshipGraph,
} from './website-relationship-graph';

export type WebsiteGraphSummary = {
  pages: number;
  assets: number;
  links: number;
  assetReferences: number;
  orphanPages: number;
  unusedAssets: number;
  sharedAssets: number;
};

export type WebsiteGraphNavigationLink = {
  targetPageId: string;
  route: string;
  title: string;
  labels: string[];
  sourcePageIds: string[];
  occurrenceCount: number;
  coverage: number;
};

export type WebsiteGraphAnalysis = {
  summary: WebsiteGraphSummary;
  primaryPages: WebsiteGraphPage[];
  orphanPages: WebsiteGraphPage[];
  globalAssets: WebsiteGraphAsset[];
  sharedStylesheets: WebsiteGraphAsset[];
  sharedScripts: WebsiteGraphAsset[];
  likelyLogoAssets: WebsiteGraphAsset[];
  likelyNavigationLinks: WebsiteGraphNavigationLink[];
};

const DEFAULT_GLOBAL_ASSET_THRESHOLD = 0.6;
const DEFAULT_NAVIGATION_THRESHOLD = 0.5;

function normalizeText(value: string): string {
  return value
    .replace(/\s+/g, ' ')
    .trim();
}

function isStylesheet(asset: WebsiteGraphAsset): boolean {
  return (
    asset.mimeType === 'text/css' ||
    /\.css(?:$|[?#])/i.test(asset.path)
  );
}

function isScript(asset: WebsiteGraphAsset): boolean {
  return (
    /javascript|ecmascript/i.test(asset.mimeType) ||
    /\.(?:js|mjs|cjs)(?:$|[?#])/i.test(asset.path)
  );
}

function isImage(asset: WebsiteGraphAsset): boolean {
  return (
    asset.mimeType.startsWith('image/') ||
    /\.(?:avif|bmp|gif|jpe?g|png|svg|webp)$/i.test(
      asset.path,
    )
  );
}

function hasLogoLikePath(asset: WebsiteGraphAsset): boolean {
  const filename =
    asset.path.split('/').pop()?.toLowerCase() ?? '';

  return /(?:^|[-_.])(logo|brand|mark|wordmark)(?:[-_.]|$)/i.test(
    filename,
  );
}

function pageCoverage(
  asset: WebsiteGraphAsset,
  pageCount: number,
): number {
  if (pageCount === 0) {
    return 0;
  }

  return asset.referencedByPages.length / pageCount;
}

function rankPrimaryPages(
  pages: WebsiteGraphPage[],
): WebsiteGraphPage[] {
  return [...pages].sort((left, right) => {
    if (left.route === '/' && right.route !== '/') {
      return -1;
    }

    if (right.route === '/' && left.route !== '/') {
      return 1;
    }

    if (right.incomingLinks !== left.incomingLinks) {
      return right.incomingLinks - left.incomingLinks;
    }

    if (right.outgoingLinks !== left.outgoingLinks) {
      return right.outgoingLinks - left.outgoingLinks;
    }

    return left.route.localeCompare(right.route);
  });
}

function collectNavigationLinks(
  graph: WebsiteRelationshipGraph,
  minimumCoverage: number,
): WebsiteGraphNavigationLink[] {
  const pageById = new Map(
    graph.pages.map((page) => [page.id, page]),
  );

  const linksByTarget = new Map<string, WebsiteGraphLink[]>();

  for (const link of graph.links) {
    const existing = linksByTarget.get(link.targetPageId) ?? [];
    existing.push(link);
    linksByTarget.set(link.targetPageId, existing);
  }

  const sourcePageCount = Math.max(graph.pages.length, 1);
  const navigationLinks: WebsiteGraphNavigationLink[] = [];

  for (const [targetPageId, links] of linksByTarget) {
    const targetPage = pageById.get(targetPageId);

    if (!targetPage) {
      continue;
    }

    const sourcePageIds = Array.from(
      new Set(links.map((link) => link.sourcePageId)),
    ).sort();

    const coverage =
      sourcePageIds.length / sourcePageCount;

    if (
      targetPage.route !== '/' &&
      coverage < minimumCoverage
    ) {
      continue;
    }

    const labels = Array.from(
      new Set(
        links
          .map((link) => normalizeText(link.text))
          .filter(Boolean),
      ),
    ).sort();

    navigationLinks.push({
      targetPageId,
      route: targetPage.route,
      title: targetPage.title,
      labels,
      sourcePageIds,
      occurrenceCount: links.length,
      coverage,
    });
  }

  return navigationLinks.sort((left, right) => {
    if (right.coverage !== left.coverage) {
      return right.coverage - left.coverage;
    }

    if (
      right.occurrenceCount !== left.occurrenceCount
    ) {
      return (
        right.occurrenceCount -
        left.occurrenceCount
      );
    }

    if (left.route === '/' && right.route !== '/') {
      return -1;
    }

    if (right.route === '/' && left.route !== '/') {
      return 1;
    }

    return left.route.localeCompare(right.route);
  });
}

export function analyzeWebsiteRelationshipGraph(
  graph: WebsiteRelationshipGraph,
  options: {
    globalAssetThreshold?: number;
    navigationThreshold?: number;
  } = {},
): WebsiteGraphAnalysis {
  const globalAssetThreshold =
    options.globalAssetThreshold ??
    DEFAULT_GLOBAL_ASSET_THRESHOLD;

  const navigationThreshold =
    options.navigationThreshold ??
    DEFAULT_NAVIGATION_THRESHOLD;

  const globalAssets = graph.assets
    .filter(
      (asset) =>
        !asset.isUnused &&
        pageCoverage(asset, graph.pages.length) >=
          globalAssetThreshold,
    )
    .sort((left, right) => {
      const coverageDifference =
        pageCoverage(right, graph.pages.length) -
        pageCoverage(left, graph.pages.length);

      if (coverageDifference !== 0) {
        return coverageDifference;
      }

      return left.path.localeCompare(right.path);
    });

  const sharedStylesheets = globalAssets.filter(
    isStylesheet,
  );

  const sharedScripts = globalAssets.filter(isScript);

  const likelyLogoAssets = graph.assets
    .filter(
      (asset) =>
        !asset.isUnused &&
        isImage(asset) &&
        hasLogoLikePath(asset),
    )
    .sort((left, right) => {
      const pageDifference =
        right.referencedByPages.length -
        left.referencedByPages.length;

      if (pageDifference !== 0) {
        return pageDifference;
      }

      return right.referenceCount - left.referenceCount;
    });

  return {
    summary: {
      pages: graph.pages.length,
      assets: graph.assets.length,
      links: graph.links.length,
      assetReferences: graph.assetReferences.length,
      orphanPages: graph.pages.filter(
        (page) => page.isOrphan,
      ).length,
      unusedAssets: graph.assets.filter(
        (asset) => asset.isUnused,
      ).length,
      sharedAssets: graph.assets.filter(
        (asset) => asset.isShared,
      ).length,
    },

    primaryPages: rankPrimaryPages(
      graph.pages.filter((page) => !page.isOrphan),
    ),

    orphanPages: graph.pages
      .filter((page) => page.isOrphan)
      .sort((left, right) =>
        left.route.localeCompare(right.route),
      ),

    globalAssets,
    sharedStylesheets,
    sharedScripts,
    likelyLogoAssets,

    likelyNavigationLinks: collectNavigationLinks(
      graph,
      navigationThreshold,
    ),
  };
}
